import { useEffect, useState } from 'react';
import { API_BASE } from '../lib/api';
import { useGps } from '../lib/gps';

interface Wx {
  tempC: number;
  windSpeed: number;
  windDir: number;
  gust: number;
  precipProb: number;
  code: number;
  updatedAt: number;
}

/**
 * Weather, from the buggy.
 *
 * Wind is the reason this tile exists. Temperature is comfort; wind direction
 * and strength change the club, and a golfer standing on a tee cannot read it
 * off the sky with any accuracy. The compass rose points the way the wind is
 * GOING, which is the way a ball gets pushed — meteorological convention gives
 * the direction it comes FROM, and quoting that to a golfer is a two-club
 * mistake in the wrong direction.
 */
export default function WeatherScreen() {
  const { fix } = useGps();
  const [wx, setWx] = useState<Wx | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!fix) return;
    let stop = false;
    const pull = async () => {
      try {
        const [lng, lat] = fix.pos;
        const r = await fetch(`${API_BASE}/api/weather?lat=${lat}&lng=${lng}`);
        if (!r.ok) throw new Error(String(r.status));
        const d = (await r.json()) as Wx;
        if (!stop) { setWx(d); setFailed(false); }
      } catch {
        if (!stop) setFailed(true);
      }
    };
    void pull();
    // A round is four hours; the sea breeze that matters on the back nine was
    // not blowing on the 1st tee.
    const t = setInterval(() => void pull(), 10 * 60 * 1000);
    return () => { stop = true; clearInterval(t); };
  }, [fix]);

  const kmh = wx ? Math.round(wx.windSpeed * 3.6) : null;
  const gust = wx ? Math.round(wx.gust * 3.6) : null;

  return (
    <div className="wx">
      <header className="wx-top">
        <a className="wx-back" href="/buggy" aria-label="Back">‹</a>
        <span>Weather</span>
      </header>

      {!fix && <p className="wx-note">Waiting for GPS to know where you are.</p>}
      {fix && !wx && !failed && <p className="wx-note">Checking the forecast…</p>}
      {failed && !wx && <p className="wx-note">Could not reach the forecast. The rest of the app is unaffected.</p>}

      {wx && (
        <>
          <div className="wx-wind">
            <div
              className="wx-rose"
              style={{ transform: `rotate(${wx.windDir}deg)` }}
              aria-hidden="true"
            >
              ↑
            </div>
            <div>
              <div className="wx-big">{kmh}<span className="wx-unit"> km/h</span></div>
              <div className="wx-note">
                gusting {gust} km/h · blowing towards {compass(wx.windDir)}
              </div>
            </div>
          </div>

          <div className="wx-rows">
            <div><span>Temperature</span><b>{Math.round(wx.tempC)}°C</b></div>
            <div><span>Chance of rain</span><b>{wx.precipProb}%</b></div>
            <div><span>Updated</span><b>{new Date(wx.updatedAt).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}</b></div>
          </div>
        </>
      )}
    </div>
  );
}

const POINTS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
                'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
const compass = (deg: number) => POINTS[Math.round(((deg % 360) / 22.5)) % 16];
